// src/components/TherapistBookingModal.js
import React, { useState } from 'react';
import { X, Clock, Calendar } from 'lucide-react';

const TherapistBookingModal = ({ isOpen, onClose, therapist }) => {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  if (!isOpen) return null;

  const handleBookingRequest = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('therapy_auth_token');
      if (!token) return;

      const requestData = {
        therapist_id: therapist?.id?.toString() || '',
        message: message || 'Jag skulle vilja boka ett samtal.',
        preferred_times: 'Flexibel med tider',
      };

      if (!requestData.therapist_id) throw new Error('Terapeut-ID saknas.');

      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE}/api/therapist-booking/request-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        setSent(true);
        setMessage('');
      } else {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Något gick fel');
      }
    } catch (error) {
      alert(`Något gick fel: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-brand-primary-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold">Boka samtal med terapeut</h2>
              <p className="text-white/75 text-sm mt-0.5">Legitimerad psykoterapeut</p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 rounded-full transition-colors">
              <X size={18} />
            </button>
          </div>
          {therapist && (
            <div className="mt-4 bg-white/10 rounded-xl px-4 py-3">
              <p className="font-semibold text-sm">{therapist.name}</p>
              <p className="text-white/70 text-xs">{therapist.email}</p>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {sent ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-brand-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Calendar className="text-brand-primary-600" size={24} />
              </div>
              <p className="font-semibold text-gray-900 mb-1">Förfrågan skickad!</p>
              <p className="text-sm text-gray-500 mb-6">Din terapeut hör av sig inom 24 timmar.</p>
              <button onClick={onClose} className="w-full py-3 bg-brand-primary-600 text-white font-semibold rounded-xl hover:bg-brand-primary-700 transition-colors">
                Stäng
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Skicka en bokningsförfrågan så hör din terapeut av sig inom 24 timmar för att boka tid.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Meddelande <span className="text-gray-400 font-normal">(valfritt)</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Beskriv kortfattat vad du vill prata om eller föreslå tider som passar..."
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary-400 focus:border-transparent resize-none text-sm mb-5"
                rows={3}
              />
              <button
                onClick={handleBookingRequest}
                disabled={isLoading}
                className="w-full py-3 bg-brand-primary-600 text-white font-semibold rounded-xl hover:bg-brand-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 mb-3"
              >
                {isLoading ? <><Clock className="animate-spin" size={16} /> Skickar...</> : 'Skicka bokningsförfrågan'}
              </button>
              <button onClick={onClose} className="w-full py-3 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                Stäng
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TherapistBookingModal;
